// Simplified map helpers for the MCP dashboard map port.
// Ported from PathMarkr-MCP-Project apps/web/src/lib/pins.ts and
// apps/web/src/components/dashboard/MapboxMap.tsx, adapted for plain JS.

import circle from '@turf/circle';

const EARTH_RADIUS_MI = 3958.8;

export function haversineMi(aLat, aLng, bLat, bLng) {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_MI * Math.asin(Math.sqrt(h));
}

export const BREADCRUMB_INTERVAL_MI = 1;
const MAX_INTERPOLATION_LEAP_MI = 3;

export function breadcrumbsAlongRoute(coordinates, intervalMi = BREADCRUMB_INTERVAL_MI) {
  if (coordinates.length === 0) return [];
  if (coordinates.length === 1) {
    return [{ lat: coordinates[0][1], lng: coordinates[0][0], offsetMi: 0 }];
  }

  const out = [];
  out.push({ lat: coordinates[0][1], lng: coordinates[0][0], offsetMi: 0 });

  let cumulativeMi = 0;
  let nextThreshold = intervalMi;

  for (let i = 1; i < coordinates.length; i++) {
    const a = coordinates[i - 1];
    const b = coordinates[i];
    const segLen = haversineMi(a[1], a[0], b[1], b[0]);
    if (segLen === 0) continue;

    if (segLen > MAX_INTERPOLATION_LEAP_MI) {
      cumulativeMi += segLen;
      const bOffset = Math.round(cumulativeMi * 100) / 100;
      const tail = out[out.length - 1];
      if (!tail || Math.abs(tail.offsetMi - bOffset) > 0.01) {
        out.push({ lat: b[1], lng: b[0], offsetMi: bOffset });
      }
      nextThreshold = cumulativeMi + intervalMi;
      continue;
    }

    const segStart = cumulativeMi;
    const segEnd = cumulativeMi + segLen;

    while (nextThreshold <= segEnd) {
      const t = (nextThreshold - segStart) / segLen;
      const lng = a[0] + (b[0] - a[0]) * t;
      const lat = a[1] + (b[1] - a[1]) * t;
      out.push({ lat, lng, offsetMi: Math.round(nextThreshold * 100) / 100 });
      nextThreshold += intervalMi;
    }

    cumulativeMi = segEnd;
  }

  const last = coordinates[coordinates.length - 1];
  const lastOffset = Math.round(cumulativeMi * 100) / 100;
  const tail = out[out.length - 1];
  if (!tail || Math.abs(tail.offsetMi - lastOffset) > 0.01) {
    out.push({ lat: last[1], lng: last[0], offsetMi: lastOffset });
  }

  return out;
}

export function buildHeatmapPoints(stops, routes) {
  const features = [];
  for (const s of stops) {
    if (!Number.isFinite(s.lat) || !Number.isFinite(s.lng)) continue;
    features.push({
      type: 'Feature',
      properties: { weight: 1.0 },
      geometry: { type: 'Point', coordinates: [s.lng, s.lat] },
    });
  }

  for (const r of routes) {
    if (!r.coordinates || r.coordinates.length === 0) continue;
    const bcs = breadcrumbsAlongRoute(r.coordinates, 0.5);
    for (const bc of bcs) {
      if (!Number.isFinite(bc.lat) || !Number.isFinite(bc.lng)) continue;
      features.push({
        type: 'Feature',
        properties: { weight: 0.3 },
        geometry: { type: 'Point', coordinates: [bc.lng, bc.lat] },
      });
    }
  }

  if (features.length > 20000) {
    const step = features.length / 20000;
    const sampled = [];
    for (let i = 0; i < 20000; i++) sampled.push(features[Math.floor(i * step)]);
    return { type: 'FeatureCollection', features: sampled };
  }
  return { type: 'FeatureCollection', features };
}

export function boundsForData(stops, routes) {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  let n = 0;

  for (const s of stops) {
    if (!Number.isFinite(s.lat) || !Number.isFinite(s.lng)) continue;
    minLng = Math.min(minLng, s.lng);
    maxLng = Math.max(maxLng, s.lng);
    minLat = Math.min(minLat, s.lat);
    maxLat = Math.max(maxLat, s.lat);
    n++;
  }

  for (const r of routes) {
    if (!r.coordinates) continue;
    for (const [lng, lat] of r.coordinates) {
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;
      minLng = Math.min(minLng, lng);
      maxLng = Math.max(maxLng, lng);
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
      n++;
    }
  }

  if (n === 0) return null;
  if (minLng === maxLng && minLat === maxLat) {
    const pad = 0.02;
    return [
      [minLng - pad, minLat - pad],
      [maxLng + pad, maxLat + pad],
    ];
  }
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
}

export function emptyFC() {
  return { type: 'FeatureCollection', features: [] };
}

function stopFeature(s, selected, focused, heatmapMode, faded, inHotZone, mode, routeSelected) {
  return {
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [s.lng, s.lat] },
    properties: {
      id: s.id,
      truckId: s.truckId,
      address: s.address || '',
      durationMin: s.durationMin || 0,
      selected: mode === 'stop' ? selected : routeSelected,
      focused,
      pathmarkrHeatmapMode: heatmapMode,
      faded,
      inHotZone,
    },
  };
}

export function stopsToGeoJson(
  stops,
  selectedIds,
  focusedId,
  heatmapMode,
  fadedStopIds,
  mode,
  selectedRouteIds,
  inHotZoneStopIds
) {
  const stopSelectedSet = new Set(selectedIds);
  const routeSelected = selectedRouteIds || new Set();
  const inHotZone = inHotZoneStopIds || new Set();
  return {
    type: 'FeatureCollection',
    features: stops.map((s) =>
      stopFeature(
        s,
        stopSelectedSet.has(s.id),
        focusedId === s.id,
        heatmapMode,
        (fadedStopIds || new Set()).has(s.id),
        inHotZone.has(s.id),
        mode,
        routeSelected.has(s.truckId)
      )
    ),
  };
}

const RING_POLYGON_STEPS = 64;

export function stopsAsRingFC(stops, selectedIds, radiusMi) {
  const selectedSet = new Set(selectedIds);
  return {
    type: 'FeatureCollection',
    features: stops.map((s) => {
      const poly = circle([s.lng, s.lat], radiusMi, { units: 'miles', steps: RING_POLYGON_STEPS });
      poly.properties = { id: s.id, selected: selectedSet.has(s.id) };
      return poly;
    }),
  };
}

export function breadcrumbsAsRingFC(breadcrumbs, radiusMi) {
  return {
    type: 'FeatureCollection',
    features: breadcrumbs.map((bc) => {
      const poly = circle([bc.lng, bc.lat], radiusMi, { units: 'miles', steps: RING_POLYGON_STEPS });
      poly.properties = { id: bc.id, selected: bc.selected ?? false };
      return poly;
    }),
  };
}

export function breadcrumbsAsFC(breadcrumbs) {
  return {
    type: 'FeatureCollection',
    features: breadcrumbs.map((bc) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [bc.lng, bc.lat] },
      properties: { id: bc.id, selected: bc.selected ?? false },
    })),
  };
}

export function routesToGeoJson(routes, trucks, selectedIds, mode) {
  const features = [];
  const selectedSet = mode === 'route' ? new Set(selectedIds) : null;
  const anySelectionExists = mode === 'route' && selectedIds.length > 0;
  for (const r of routes) {
    const truck = (trucks || []).find((t) => t.id === r.truckId);
    const color = truck?.color || '#0F1729';
    const selected = selectedSet?.has(r.id) ?? false;
    const dimmed = !selected && anySelectionExists;
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: r.coordinates },
      properties: { id: r.id, truckId: r.truckId, color, selected, dimmed, kind: 'gps' },
    });
  }
  return { type: 'FeatureCollection', features };
}

export function clusterStopsToHotZones(stops, radiusMi = 1) {
  const groups = [];
  for (const s of stops) {
    let placed = false;
    for (const g of groups) {
      const head = g[0];
      if (haversineMi(s.lat, s.lng, head.lat, head.lng) <= radiusMi) {
        g.push(s);
        placed = true;
        break;
      }
    }
    if (!placed) groups.push([s]);
  }

  return groups.map((g, i) => {
    const lat = g.reduce((sum, s) => sum + s.lat, 0) / g.length;
    const lng = g.reduce((sum, s) => sum + s.lng, 0) / g.length;
    const totalDurationMin = g.reduce((sum, s) => sum + (s.durationMin || 0), 0);
    return {
      id: `zone-${i}`,
      lat,
      lng,
      stopIds: g.map((s) => s.id),
      visitCount: g.length,
      totalDurationMin,
    };
  });
}
